import React, { useState } from 'react';
import { 
  FaChartPie, FaShieldAlt, FaBalanceScale, FaCheckCircle, 
  FaChevronDown, FaLandmark, FaBriefcase, FaChartLine, FaUsers 
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const AssetManagement = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What is Asset Management and how does it differ from wealth management?",
      a: "Asset management focuses specifically on the creation, growth, and optimization of your investment portfolio—buying and selling assets like equities, debt, mutual funds, and alternatives to maximize returns. Wealth management is a broader, holistic service that encompasses asset management alongside tax planning, estate planning, insurance, and retirement strategies."
    },
    {
      q: "What is Strategic Asset Allocation?",
      a: "Strategic Asset Allocation is a portfolio design framework where we determine the ideal mix of asset classes (such as equities, fixed income, real estate, and alternatives) tailored to your specific financial goals, time horizon, and risk tolerance, ensuring your portfolio is balanced for long-term success."
    },
    {
      q: "How often do you monitor and rebalance managed portfolios?",
      a: "We continuously monitor market indicators, economic trends, and asset performance. Portfolios are systematically rebalanced whenever market fluctuations cause your asset allocation to drift significantly from your target risk profile, ensuring disciplined risk management."
    },
    {
      q: "Who can benefit from professional Asset Management services?",
      a: "Asset management is ideal for high-net-worth individuals, busy professionals, business owners, and families who want professional, research-driven oversight of their investments without having to manage day-to-day market volatility themselves."
    }
  ];

  return (
    <main className="asset-management-page">
      <Helmet>
  {/* Basic SEO */}
  <title>Asset Management Services in Hyderabad | Investment & Wealth Management | Lotoswealth</title>

  <meta
    name="description"
    content="Lotoswealth offers professional Asset Management Services in Hyderabad, providing customized investment strategies, portfolio management, wealth preservation, risk management, mutual funds, equity investments, and long-term financial planning for individuals, families, NRIs, and businesses."
  />

  <meta
    name="keywords"
    content="Asset Management Services Hyderabad, Asset Management Hyderabad, Investment Management Hyderabad, Wealth Management Hyderabad, Portfolio Management Hyderabad, Portfolio Management Services Hyderabad, Investment Advisor Hyderabad, Financial Advisor Hyderabad, Wealth Advisor Hyderabad, Mutual Funds Hyderabad, Equity Investments Hyderabad, Stocks Investment Hyderabad, Investment Planning Hyderabad, Wealth Preservation Hyderabad, Risk Management Hyderabad, Financial Planning Hyderabad, Retirement Planning Hyderabad, High Net Worth Investment Hyderabad, HNI Wealth Management Hyderabad, Asset Allocation Hyderabad, Lotoswealth"
  />

  <meta name="robots" content="index, follow" />
  <meta name="author" content="Lotoswealth" />
  <meta name="language" content="English" />

  <link
    rel="canonical"
    href="https://www.lotoswealth.com/asset-management"
  />

  {/* Open Graph */}
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="Lotoswealth" />
  <meta
    property="og:url"
    content="https://www.lotoswealth.com/asset-management"
  />
  <meta
    property="og:title"
    content="Asset Management Services in Hyderabad | Lotoswealth"
  />
  <meta
    property="og:description"
    content="Grow and protect your wealth with Asset Management Services in Hyderabad. Lotoswealth provides personalized investment management, portfolio optimization, risk management, and long-term wealth creation."
  />
  <meta
    property="og:image"
    content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
  />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta
    name="twitter:title"
    content="Asset Management Services in Hyderabad | Lotoswealth"
  />
  <meta
    name="twitter:description"
    content="Expert Asset Management Services in Hyderabad. Personalized portfolio management, wealth preservation, investment planning, and financial advisory for long-term financial success."
  />
  <meta
    name="twitter:image"
    content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
  />
</Helmet>
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>
        
        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img 
            src="/images/asset-management.jpg" 
            alt="Asset Management Services" 
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Strategic Asset Management
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Preserving Capital While Maximizing Returns Through Disciplined Strategies
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. Effective asset management is the bedrock of long-term financial security. Our professional asset management services focus on optimizing your investment portfolios through rigorous research, strategic asset allocation, and active risk mitigation.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            We navigate complex market cycles on your behalf, balancing growth opportunities across equities, debt, and alternative assets with robust capital protection frameworks. Whether you are managing personal wealth or structuring a family office, our experienced portfolio managers deliver disciplined execution.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Entrust your investments to seasoned professionals who prioritize your financial objectives and ensure your portfolio remains resilient against market volatility.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
            
            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY ASSET MANAGEMENT MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Why Professional Asset Management is Essential
              </h2>
              <p className="text-muted line-height-lg">
                Modern financial markets are fast-moving, highly interconnected, and constantly influenced by global economic shifts. Managing a substantial portfolio requires continuous research, emotional discipline, and specialized expertise that goes far beyond casual investing.
              </p>
              <p className="text-muted line-height-lg">
                Professional asset management removes the guesswork. By applying data-driven research, structured asset allocation, and proactive risk controls, we ensure your capital is efficiently distributed to capture upside growth while actively safeguarding against downside risks.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Core Pillars of Our Approach</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Strategic Allocation:</strong> Balancing growth assets with defensive holdings.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Risk Mitigation:</strong> Proactive downside protection across market cycles.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Research-Driven:</strong> Institutional evaluation of companies and sectors.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Active Monitoring:</strong> Ongoing rebalancing to maintain target risk profiles.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR ASSET SERVICES (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">📊 Our Asset Management Services</h2>
            <p className="text-white">Comprehensive solutions designed to protect and compound your wealth.</p>
          </div>

          <div className="row g-4">
            
            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBalanceScale size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Strategic Asset Allocation</h5>
                <p className="text-muted small">Customizing the ideal mix of equities, fixed income, cash, and alternative investments aligned with your personal risk tolerance and financial horizon.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaChartPie size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Portfolio Diversification</h5>
                <p className="text-muted small">Spreading investments across uncorrelated sectors, market caps, and asset classes to systematically reduce concentration risk and enhance stability.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Wealth Protection Strategies</h5>
                <p className="text-muted small">Implementing robust capital preservation frameworks, defensive hedging, and risk-managed instruments to insulate your wealth during market corrections.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBriefcase size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Family Office Services</h5>
                <p className="text-muted small">Bespoke, institutional-grade asset management and governance for ultra-high-net-worth families, coordinating multi-generational wealth preservation.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaChartLine size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Performance Monitoring & Reporting</h5>
                <p className="text-muted small">Providing transparent, technology-driven dashboards and regular performance reviews to track asset growth against established benchmarks.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaLandmark size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Tactical Rebalancing</h5>
                <p className="text-muted small">Opportunistically adjusting asset weights in response to shifting macroeconomic trends, interest rate cycles, and market valuations.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP MANAGEMENT PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 Our Asset Management Process</h2>
            <p className="text-light opacity-75">A disciplined framework designed to secure and grow your portfolio.</p>
          </div>

          <div className="row g-4">
            
            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Profiling & Discovery</h5>
                <p className="small text-muted mb-0">We analyze your financial goals, liquidity needs, investment horizon, and risk appetite to establish a baseline profile.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – Asset Allocation Design</h5>
                <p className="small text-muted mb-0">Formulating a strategic asset mix across equities, fixed income, and alternatives tailored precisely to your objectives.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Portfolio Construction</h5>
                <p className="small text-muted mb-0">Selecting high-quality securities, funds, and instruments backed by rigorous fundamental research and valuation studies.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – Active Risk Monitoring</h5>
                <p className="small text-muted mb-0">Continuously tracking market conditions, corporate earnings, and macroeconomic indicators to protect your capital.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Periodic Rebalancing</h5>
                <p className="small text-muted mb-0">Periodically trimming outperforming assets and reallocating capital to maintain your target risk-reward balance.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">
            
            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Benefits from Asset Management?</h3>
              <p className="text-muted small mb-4">Our professional management services cater to discerning investors:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "High Net Worth Individuals (HNIs)", "Ultra-HNIs & Family Offices",
                  "Business Owners & Entrepreneurs", "Corporate Executives",
                  "Successful Professionals", "NRIs Managing Indian Portfolios",
                  "Retirees with Substantial Corpus", "Long-Term Wealth Builders"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Trust Lotoswealth?</h3>
                <p className="text-muted small mb-4">Managing assets requires research diligence, fiduciary responsibility, and consistent discipline. Clients choose us because we offer:</p>
                <div className="row g-3">
                  {[
                    "Research-Driven Investment Decisions", "Disciplined Risk Management Expertise",
                    "Transparent Reporting & Dashboards", "Customized Strategic Asset Allocation",
                    "Dedicated Relationship Managers", "Active Portfolio Monitoring",
                    "Ethical & Client-Centric Practices", "Long-Term Value Creation Focus"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Asset Management & Portfolio Strategies</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button 
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown 
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`} 
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Ready to Optimize Your Investment Portfolio?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Take a disciplined, research-backed approach to growing and preserving your wealth. Connect with our asset management experts today.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Schedule an Asset Management Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default AssetManagement;